import { CustomLink } from "@/components/links/nav-link"
import { useNavigationWithSearch } from "@/hooks/useNavigationWithSearch"
import type { PaginationProps } from "antd"
import { ConfigProvider, Empty, Pagination, Select, Spin } from "antd"
import { FC, useState } from "react"
import { CSVLink } from "react-csv"
import { CSSTransition, SwitchTransition } from "react-transition-group"
import { useAuth } from "../../../../../../../components/auth"
import {
  useGetNotificationDescriptionsLazyQuery,
  useGetNotificationTitlesLazyQuery,
} from "../../../../../../../graphql"
import { DownloadIcon, EditDots, PlusIcon, SearchIcon } from "../../../../../../../icons"
import { formatDate, removeSelectDuplicates } from "../../../../../../../utils"
import "./index.less"

const List: FC<NotificationListInterface> = ({
  list,
  page,
  loading,
  setPage,
  filters,
  pageSize,
  setFilters,
  totalItems,
  setLoading,
  setPageSize,
  exportLoading,
  listForExport,
  downloadBtnRef,
  createExportList,
  createQueryFilters,
}) => {
  const { permissions } = useAuth()
  const blockPermissions = permissions?.firstMenu
    ?.find(item => item.name == "Content")
    ?.blocks?.find((block: any) => block.name == "Notifications")

  const { navigateWithSearch } = useNavigationWithSearch()

  const [getNotificationTitles] = useGetNotificationTitlesLazyQuery()
  const [getNotificationDescriptions] = useGetNotificationDescriptionsLazyQuery()

  const [titles, setTitles] = useState<SelectItems>()
  const [descriptions, setDescriptions] = useState<SelectItems>()

  const onChangePage: PaginationProps["onChange"] = (pageNumber: number) => {
    setPage(pageNumber)
  }

  const onChangePageSize: PaginationProps["onShowSizeChange"] = (_, pageSize: number) => {
    setPageSize(pageSize)
  }

  const filteredOption = (input: string, option: { label: string; value: string; key?: number }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())

  const getItems = async (type: string) => {
    setLoading(true)

    const queryOptions: any = {
      fetchPolicy: "network-only",
      variables: {
        input: {
          ...createQueryFilters(),
          page: 1,
          pageSize: 1000,
        },
      },
    }

    switch (type) {
      case "title":
        const titlesData = await getNotificationTitles(queryOptions)
        setTitles(
          removeSelectDuplicates(
            titlesData?.data?.getNotificationList?.data
              ?.map((item: any, i: number) => ({
                value: item?.title!,
                label: item?.title!,
                key: String(i),
              }))
              .filter((item: any) => item.value)!
          ).sort((a, b) => a.value!.localeCompare(b.value!))
        )
        break

      case "description":
        const descriptionsData = await getNotificationDescriptions(queryOptions)
        setDescriptions(
          removeSelectDuplicates(
            descriptionsData?.data?.getNotificationList?.data
              ?.map((item: any, i: number) => ({
                value: item?.body!,
                label: item?.body!,
                key: String(i),
              }))
              .filter((item: any) => item.value)!
          ).sort((a, b) => a.value!.localeCompare(b.value!))
        )
        break
    }

    setLoading(false)
  }

  const handleFilter = (data: string, type: string) => {
    switch (type) {
      case "title":
        setFilters({
          ...filters,
          selectedTitle: data,
        })
        break

      case "description":
        setFilters({
          ...filters,
          selectedDescription: data,
        })
        break
    }
  }

  const createSelectElement = (
    type: string,
    name: string,
    query: () => void,
    loading: boolean,
    options: SelectItems
  ) => (
    <div
      className={`${type?.toLowerCase().replace(" ", "-")} ${
        ((type == "title" && filters?.selectedTitle !== "All") ||
          (type == "description" && filters?.selectedDescription !== "All")) &&
        "active"
      }`}
      children={
        <Select
          showSearch
          onDropdownVisibleChange={query}
          value={name}
          optionFilterProp={"children"}
          onChange={data => handleFilter(data, type)}
          filterOption={filteredOption as any}
          options={options}
          defaultActiveFirstOption={true}
          notFoundContent={
            loading || !options ? (
              <div className={"no-data"} children={<Spin />} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className={"no-data"} />
            )
          }
          suffixIcon={<SearchIcon />}
        />
      }
    />
  )

  const handleCreateNewClick = () => {
    navigateWithSearch("/account/content/notifications/new-notification")
  }

  return (
    <SwitchTransition mode={"out-in"}>
      <CSSTransition
        key={JSON.stringify(filters) + String(page) + String(pageSize)}
        timeout={250}
        classNames={"fade"}
        appear={true}
        unmountOnExit
      >
        <div className={"main-list-wrapper notifications-list"}>
          <ConfigProvider
            theme={{
              components: {
                Pagination: {
                  colorPrimary: "black",
                  colorPrimaryBorder: "black",
                  colorPrimaryHover: "black",
                  controlOutline: "#fff",
                },
                Select: {
                  colorText: "#677685",
                  colorPrimaryBorder: "#fff",
                  colorPrimaryHover: "#fff",
                  controlOutline: "#fff",
                  colorBgContainerDisabled: "#fff",
                },
              },
            }}
          >
            <div className={"list-wrapper"}>
              <div className={"items top"}>
                {createSelectElement("title", "TITLE", () => getItems("title"), loading, titles!)}
                {createSelectElement(
                  "description",
                  "DESCRIPTION",
                  () => getItems("description"),
                  loading,
                  descriptions!
                )}
                <div children={"Push At"} />
                <div children={"TYPE"} />
                <div className={"centered"} children={"EDIT"} />
              </div>

              {list &&
                list.map((item: any, i: number) => (
                  <div className={"items"} key={`list-item-${i}`}>
                    <div children={<p>{item?.title || "-"}</p>} />
                    <div children={<p>{item?.body || "-"}</p>} />
                    <div children={<p>{formatDate(item?.pushAt || item?.createdAt) || "-"}</p>} />
                    <div children={!item?.isAutoGenerated ? "Custom" : item?.type?.name || "-"} />
                    <div
                      className={`centered ${(item?.isAutoGenerated || !blockPermissions?.edit) && "disabled"}`}
                      children={
                        <CustomLink
                          to={item?.isAutoGenerated || !blockPermissions?.edit ? "#" : item.id}
                          children={<EditDots />}
                        />
                      }
                    />
                  </div>
                ))}

              {!list && (
                <div className={"spinner-wrapper"}>
                  <Spin />
                </div>
              )}

              {list?.length == 0 && (
                <div className={"empty"}>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )}
            </div>

            <div
              className={"navigation"}
              children={
                <Pagination
                  showQuickJumper
                  current={page}
                  pageSize={pageSize}
                  total={totalItems}
                  onChange={onChangePage}
                  onShowSizeChange={onChangePageSize}
                />
              }
            />

            {blockPermissions && (
              <button onClick={handleCreateNewClick} className={"create-new"}>
                New Notification
                <PlusIcon />
              </button>
            )}

            <button
              onClick={() => createExportList()}
              className={`download ${!blockPermissions?.edit && "single"}`}
              disabled={!list}
            >
              Download CSV
              {exportLoading ? <Spin /> : <DownloadIcon />}
            </button>

            <CSVLink data={listForExport || []} filename={"posts"} ref={downloadBtnRef as any} />
          </ConfigProvider>
        </div>
      </CSSTransition>
    </SwitchTransition>
  )
}

export default List
